import { extensionCatalog, type DigitilesExtension } from '../catalog';
import { watchActiveExtensionNodes, type ActiveExtensionNode } from '../activeNodes';
import { watchExtensionOutputs } from '../outputs';
import type { LogicSimulation } from '@/models/logic/LogicSimulation';
import { ByteQueue } from './ByteQueue';
import {
  WS_BIT,
  WS_READY,
  WS_ADVANCE,
  defaults,
  runtimeSchema,
  socketContributions,
  type Runtime,
} from './tiles';
export { WS_BIT, WS_READY, WS_ADVANCE } from './tiles';
interface Channel {
  queue: ByteQueue;
  current: number;
  nodes: ActiveExtensionNode[];
}
interface Connection {
  socket: WebSocket;
  channels: Set<Channel>;
}
export const websocketExtension: DigitilesExtension = {
  id: 'digitiles.websocket',
  name: 'WebSocket bytes',
  version: '1.0.0',
  description:
    'Receive bytes into a bounded queue. A ready output and an advance input let the circuit consume one byte at a time; bit tiles expose the current byte.',
  capabilities: ['network'],
  tiles: socketContributions,
  nodes: [WS_BIT, WS_READY, WS_ADVANCE].map((type) => ({
    type,
    stateSchema: runtimeSchema.transform((state) => ({
      ...state,
      enabled: false,
      value: false,
    })),
    configurationKeys: ['endpoint', 'bit', 'enabled'],
    createDefaultState: (): Runtime => ({ ...defaults(), enabled: false, value: false }),
    process: (input: boolean, state: Runtime) =>
      type === WS_ADVANCE ? input : state.enabled && state.value,
  })),
  activate(app, signal) {
    let channels = new Map<LogicSimulation, Map<string, Channel>>();
    const connections = new Map<string, Connection>();
    const report = (message: string) => {
      if (!signal.aborted) extensionCatalog.setError('digitiles.websocket', message);
    };
    const assign = (target: ActiveExtensionNode, enabled: boolean, value: boolean) => {
      if (target.node.type === WS_ADVANCE) return;
      const state = target.node.state as Runtime;
      if (state.enabled === enabled && state.value === value) return;
      state.enabled = enabled;
      state.value = value;
      if (target.simulation.graph.nodes.get(target.node.id) === target.node)
        target.simulation.triggerUpdate(target.node.id);
    };
    const publish = (channel: Channel) => {
      for (const target of channel.nodes)
        assign(
          target,
          true,
          target.node.type === WS_READY
            ? channel.queue.length > 0
            : (channel.current & (1 << (target.node.state as Runtime).bit)) !== 0,
        );
    };
    const close = (connection: Connection) => {
      connection.socket.onmessage = null;
      connection.socket.onerror = null;
      connection.socket.onclose = null;
      connection.socket.close();
    };
    const closeAll = () => {
      for (const connection of connections.values()) close(connection);
      connections.clear();
    };
    signal.addEventListener('abort', closeAll, { once: true });
    const unwatch = watchActiveExtensionNodes(
      app,
      [WS_BIT, WS_READY, WS_ADVANCE],
      (targets) => {
        const next = new Map<LogicSimulation, Map<string, Channel>>();
        const endpoints = new Map<string, Set<Channel>>();
        const retainedNodes = new Set(targets.map((target) => target.node));
        for (const groups of channels.values())
          for (const channel of groups.values())
            for (const target of channel.nodes)
              if (!retainedNodes.has(target.node)) assign(target, false, false);
        for (const target of targets) {
          const raw = (target.node.state as Runtime).endpoint;
          if (!raw) {
            assign(target, false, false);
            continue;
          }
          const endpoint = new URL(raw).href;
          let groups = next.get(target.simulation);
          if (!groups) next.set(target.simulation, (groups = new Map()));
          let channel = groups.get(endpoint);
          if (!channel) {
            const previous = channels.get(target.simulation)?.get(endpoint);
            channel = {
              queue: previous?.queue ?? new ByteQueue(),
              current: previous?.current ?? 0,
              nodes: [],
            };
            groups.set(endpoint, channel);
            let receivers = endpoints.get(endpoint);
            if (!receivers) endpoints.set(endpoint, (receivers = new Set()));
            receivers.add(channel);
          }
          channel.nodes.push(target);
        }
        channels = next;
        for (const [endpoint, connection] of connections)
          if (!endpoints.has(endpoint)) {
            close(connection);
            connections.delete(endpoint);
          }
        for (const [endpoint, receivers] of endpoints) {
          const existing = connections.get(endpoint);
          if (existing) existing.channels = receivers;
          else if (connections.size < 16 && !signal.aborted) {
            try {
              const socket = new WebSocket(endpoint);
              socket.binaryType = 'arraybuffer';
              const connection: Connection = { socket, channels: receivers };
              connections.set(endpoint, connection);
              socket.onmessage = (event) => {
                if (signal.aborted || connections.get(endpoint) !== connection) return;
                if (
                  (typeof event.data === 'string' && event.data.length > 1_048_576) ||
                  (event.data instanceof ArrayBuffer && event.data.byteLength > 1_048_576)
                ) {
                  report('A WebSocket frame exceeded the 1 MiB limit and was discarded.');
                  return;
                }
                const bytes =
                  typeof event.data === 'string'
                    ? new TextEncoder().encode(event.data)
                    : event.data instanceof ArrayBuffer
                      ? new Uint8Array(event.data)
                      : undefined;
                if (!bytes || bytes.length > 1_048_576) {
                  report('Unsupported or oversized WebSocket frame was discarded.');
                  return;
                }
                let dropped = 0;
                for (const channel of connection.channels) {
                  dropped += channel.queue.push(bytes);
                  publish(channel);
                }
                if (dropped)
                  report(
                    'WebSocket queue full (4096 bytes). Excess incoming bytes were discarded; advance the circuit to consume queued data.',
                  );
              };
              socket.onerror = () =>
                report(
                  'WebSocket connection failed. Check its URL and server, then disable and enable live I/O to retry.',
                );
              socket.onclose = () =>
                report(
                  'WebSocket connection closed. Queued bytes remain available; disable and enable live I/O to reconnect.',
                );
            } catch (error) {
              report(
                `Unable to open WebSocket: ${error instanceof Error ? error.message : String(error)}`,
              );
            }
          } else if (!existing)
            report('WebSocket live input is limited to 16 distinct URLs.');
          for (const channel of receivers) publish(channel);
        }
      },
    );
    const stopAdvance = watchExtensionOutputs(app, WS_ADVANCE, (target, high) => {
      if (!high || signal.aborted) return;
      const endpoint = (target.node.state as Runtime).endpoint;
      if (!endpoint) return;
      const channel = channels.get(target.simulation)?.get(new URL(endpoint).href);
      const value = channel?.queue.shift();
      if (channel && value !== undefined) {
        channel.current = value;
        publish(channel);
      }
    });
    return () => {
      stopAdvance();
      unwatch();
      closeAll();
      channels.clear();
    };
  },
};
